import { memo, useEffect, useMemo, useRef, useState } from "react";
import { ProductListLayout } from "../../../../../../customComponents/ProductListLayout/ProductListLayout";
import ProductListRowCard from "../../../../../../customComponents/ProductListRowCard/ProductListRowCard";
import CartCardViewLayout from "../CartCardViewLayout/CartCardViewLayout";
import cartFallbackImage from "../../../../../../assets/images/cart-food-fallback-card-layout.png";
import CartItemDrawer from "../cartItemTable/Drawer/CartItemDrawer/CartItemDrawer";

const getPriceConfig = () => {
  try {
    const appConfig = JSON.parse(localStorage.getItem("appConfig") || "{}");
    const cfg = appConfig?.all?.SHOW_PRICE_ON_MENU_ITEM_CARD;
    const settings = cfg?.storeConfigSettings?.settings || {};
    // Default to TRUE — show price unless the flag is explicitly set to false
    const showPriceOnMenu = cfg?.flag !== false;
    const priceDisplayMode =
      settings?.FIRST_VARIANT_PRICE === "enable" ? "FIRST_VARIANT" : "RANGE";
    return { showPriceOnMenu, priceDisplayMode };
  } catch {
    return { showPriceOnMenu: true, priceDisplayMode: "RANGE" };
  }
};

const CartProductSection = ({
  layoutType,
  loader,
  productsRefreshing = false,
  searchLoader,
  particularCategoryItems,
  retailType,
  color,
  complimentaryMode,
  increseItemQuantityTroughParticularCategory,
  addItemThroughParticularCategoryItemLoader,
  setAddOnModalOpen,
  setAddOnProductList,
  setVariantProductList,
  setSelectedProductForCart,
  saleMode,
  focusedProductIndex = -1,
  handleProductAction,
  observerTargetIndex = -1,
  setObserverTargetRef,
}) => {
  const { showPriceOnMenu, priceDisplayMode } = useMemo(() => getPriceConfig(), []);

  // Product detail drawer (lookup mode — retail only)
  const [detailProduct, setDetailProduct] = useState(null);

  // Auto-scroll the keyboard-focused card into view
  const focusedItemRef = useRef(null);
  useEffect(() => {
    if (focusedProductIndex >= 0 && focusedItemRef.current) {
      focusedItemRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [focusedProductIndex]);

  const computeDisplayPrice = (ele, hasMultipleVariants, sellingPrice) => {
    if (!showPriceOnMenu) return null;
    if (!hasMultipleVariants || ele?.isBundle) return sellingPrice;
    if (priceDisplayMode === "FIRST_VARIANT") return sellingPrice;
    // RANGE: compute min-max across all variants
    const prices = (ele.variants || [])
      .map((v) => {
        const ch = v?.externalSalesChannels?.find((c) => c?.salesChannelName === saleMode);
        return Number(ch?.salePrice || v?.inventorySync?.sellingPrice || 0);
      })
      .filter((p) => p > 0);
    if (prices.length === 0) return sellingPrice;
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    return min === max ? min : `₹${min} - ₹${max}`;
  };

  return (
    <div
      aria-busy={loader || searchLoader || productsRefreshing}
      className={
        layoutType === "rowViewLayout"
          ? "productListInnerContainer"
          : layoutType === "listViewLayout"
          ? "productListInnerContainer-list"
          : "productListInnerContainer-flex-row-wrap"
      }
    >
      {productsRefreshing && <div className="product-refresh-indicator" aria-hidden="true" />}
      {loader || searchLoader ? (
        Array.from({ length: layoutType === "listViewLayout" ? 5 : 6 }).map((_, index) => (
          <div
            key={`product-skeleton-${index}`}
            className={`product-card-skeleton product-card-skeleton--${layoutType}`}
            aria-hidden="true"
          >
            <div className="product-card-skeleton__image" />
            <div className="product-card-skeleton__body">
              <div className="product-card-skeleton__title-row">
                <div className="product-card-skeleton__line product-card-skeleton__line--title" />
                {retailType === "RESTAURANT" && <div className="product-card-skeleton__dot" />}
              </div>
              <div className="product-card-skeleton__line product-card-skeleton__line--short" />
              <div className="product-card-skeleton__footer-row">
                <div className="product-card-skeleton__price" />
                <div className="product-card-skeleton__btn" />
              </div>
            </div>
          </div>
        ))
      ) : particularCategoryItems?.length > 0 ? (
        <>
          {particularCategoryItems?.map((ele, index) => {
            const name = retailType === "RESTAURANT" ? ele?.name : ele?.variant?.name || ele?.name;
            const barcodes =
              retailType === "RESTAURANT"
                ? ele?.variants?.[0]?.barcodes?.[0]
                : ele?.variant?.barcodes?.[0];
            const selectedSaleChannel =
              retailType === "RESTAURANT"
                ? ele?.isBundle
                  ? ele?.externalSalesChannels?.find(
                      (channel) => channel?.salesChannelName === saleMode
                    )
                  : ele?.variants?.[0]?.externalSalesChannels?.find(
                      (channel) => channel?.salesChannelName === saleMode
                    )
                : null;
            const sellingPrice =
              retailType === "RESTAURANT"
                ? ele?.isBundle
                  ? selectedSaleChannel?.salePrice ?? ele?.bundlePrice ?? null
                  : selectedSaleChannel?.salePrice || ele?.variants?.[0]?.inventorySync?.sellingPrice
                : ele?.variant?.inventorySync?.mrp;
            const isSingleVaraintAndNoAddOns =
              ele?.variants?.length === 1 &&
              (!ele?.variants?.[0]?.addOn || ele?.variants?.[0]?.addOn?.length === 0);
            const hasMultipleVariants = retailType === "RESTAURANT" && ele?.variants?.length > 1;

            // Check if variant is out of stock in the current sales channel
            const isOutOfStock =
              retailType === "RESTAURANT"
                ? hasMultipleVariants
                  ? ele?.variants?.every((v) => {
                      const channel = v?.externalSalesChannels?.find(
                        (ch) => ch?.salesChannelName === saleMode
                      );
                      return channel?.stockStatus === "OUT_OF_STOCK";
                    })
                  : selectedSaleChannel?.stockStatus === "OUT_OF_STOCK"
                : false;

            const getRestaurantImage = () => {
              const img =
                ele?.variants?.[0]?.images?.front ||
                ele?.variants?.[0]?.images?.back ||
                ele?.variants?.[0]?.images?.left ||
                ele?.variants?.[0]?.images?.right ||
                ele?.variants?.[0]?.images?.top ||
                ele?.variants?.[0]?.images?.bottom ||
                ele?.variants?.[0]?.images?.top_left ||
                ele?.variants?.[0]?.images?.top_right;

              // If image exists → return it
              if (img) return img;

              return cartFallbackImage;
            };

            const getRetailImage = () => {
              return (
                ele?.variant?.images?.front ||
                ele?.variant?.images?.back ||
                ele?.variant?.images?.left ||
                ele?.variant?.images?.right ||
                ele?.variant?.images?.top ||
                ele?.variant?.images?.bottom ||
                ele?.variant?.images?.top_left ||
                ele?.variant?.images?.top_right ||
                cartFallbackImage
              );
            };

            const image = retailType === "RESTAURANT" ? getRestaurantImage() : getRetailImage();

            const displayPrice = computeDisplayPrice(ele, hasMultipleVariants, sellingPrice);
            // Multi-variant non-bundle items always show "More Variants" (price is per-variant)
            // Single-variant items show the item-level price
            const showMultipleVariantsLabel = hasMultipleVariants && !ele?.isBundle;

            const isKeyboardFocused = index === focusedProductIndex;
            const isObserverTarget = index === observerTargetIndex;

            return (
              <div
                key={
                  (retailType === "RESTAURANT"
                    ? ele?.variants?.[0]?.variantId ||
                      ele?.id ||
                      ele?.variants?.[0]?.barcodes?.[0]
                    : ele?.variant?.variantId || ele?.variant?.barcodes?.[0]) ??
                  `product-${index}`
                }
                ref={
                  isKeyboardFocused && isObserverTarget
                    ? (node) => {
                        focusedItemRef.current = node;
                        setObserverTargetRef?.(node);
                      }
                    : isKeyboardFocused
                    ? focusedItemRef
                    : isObserverTarget
                    ? setObserverTargetRef
                    : null
                }
                className={`particularCategoryEachItemCFS ${color === "dark" ? "dark" : ""}${isKeyboardFocused ? " keyboard-focused" : ""}`}
              >
                <div className="loaderAndContent">
                  {layoutType === "rowViewLayout" ? (
                    <ProductListLayout
                      loading={addItemThroughParticularCategoryItemLoader === index}
                      complimentaryMode={complimentaryMode}
                      title={name}
                      price={displayPrice}
                      hasMultipleVariants={showMultipleVariantsLabel}
                      img={image}
                      alt={"err"}
                      shortCode={retailType === "RESTAURANT" ? ele?.variants?.[0]?.shortCode : ""}
                      foodType={ele?.attributes?.foodType}
                      isBundle={ele?.isBundle}
                      bundleVariants={ele?.isBundle ? ele?.variants : null}
                      isOutOfStock={isOutOfStock}
                      itemAvailabilities={
                        retailType === "RESTAURANT" ? ele?.variants?.[0]?.itemAvailabilities : null
                      }
                      onTileClick={
                        barcodes
                          ? () => setDetailProduct({ gtin: barcodes, productName: name, productImage: image })
                          : undefined
                      }
                      onClick={() => {
                        if (retailType === "RESTAURANT") {
                          handleProductAction(ele, index);
                        } else {
                          increseItemQuantityTroughParticularCategory(
                            ele?.variant?.barcodes?.[0], ele?.variant?.name || ele?.name, index,
                            ele?.variant?.needsWeighingScaleIntegration === true ? "weighingScale" : "Standard",
                            ele?.variant?.sellingUnits?.[0]?.unit
                          );
                        }
                      }}
                    />
                  ) : layoutType === "listViewLayout" ? (
                    <ProductListRowCard
                      loading={addItemThroughParticularCategoryItemLoader === index}
                      complimentaryMode={complimentaryMode}
                      title={name}
                      price={displayPrice || sellingPrice}
                      hasMultipleVariants={showMultipleVariantsLabel}
                      img={image}
                      alt={"err"}
                      shortCode={retailType === "RESTAURANT" ? ele?.variants?.[0]?.shortCode : ""}
                      foodType={ele?.attributes?.foodType}
                      isBundle={ele?.isBundle}
                      bundleVariants={ele?.isBundle ? ele?.variants : null}
                      isOutOfStock={isOutOfStock}
                      itemAvailabilities={
                        retailType === "RESTAURANT" ? ele?.variants?.[0]?.itemAvailabilities : null
                      }
                      onTileClick={
                        barcodes
                          ? () => setDetailProduct({ gtin: barcodes, productName: name, productImage: image })
                          : undefined
                      }
                      onClick={() => {
                        if (retailType === "RESTAURANT") {
                          handleProductAction(ele, index);
                        } else {
                          increseItemQuantityTroughParticularCategory(
                            ele?.variant?.barcodes?.[0], ele?.variant?.name || ele?.name, index,
                            ele?.variant?.needsWeighingScaleIntegration === true ? "weighingScale" : "Standard",
                            ele?.variant?.sellingUnits?.[0]?.unit
                          );
                        }
                      }}
                    />
                  ) : (
                    // cardViewLayout / gridViewLayout
                    <CartCardViewLayout
                      loading={addItemThroughParticularCategoryItemLoader === index}
                      layoutType={layoutType}
                      color={color}
                      complimentaryMode={complimentaryMode}
                      title={name}
                      price={displayPrice || sellingPrice}
                      saleMode={saleMode}
                      hasMultipleVariants={showMultipleVariantsLabel}
                      img={image}
                      alt={"err"}
                      shortCode={retailType === "RESTAURANT" ? ele?.variants?.[0]?.shortCode : ""}
                      foodType={ele?.attributes?.foodType}
                      isBundle={ele?.isBundle}
                      bundleVariants={ele?.isBundle ? ele?.variants : null}
                      isOutOfStock={isOutOfStock}
                      itemAvailabilities={
                        retailType === "RESTAURANT"
                          ? ele?.variants?.[0]?.itemAvailabilities
                          : null
                      }
                      onTileClick={
                        barcodes
                          ? () => setDetailProduct({ gtin: barcodes, productName: name, productImage: image })
                          : undefined
                      }
                      onClick={() => {
                        if (retailType === "RESTAURANT") {
                          handleProductAction(ele, index);
                        } else {
                          increseItemQuantityTroughParticularCategory(
                            ele?.variant?.barcodes?.[0],
                            ele?.variant?.name || ele?.name,
                            index,
                            ele?.variant?.needsWeighingScaleIntegration === true
                              ? "weighingScale"
                              : "Standard",
                            ele?.variant?.sellingUnits?.[0]?.unit
                          );
                        }
                      }}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </>
      ) : (
        <div className={`pos-empty-state ${color === "dark" ? "dark" : ""}`}>
          <div className="pos-empty-state__icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              {retailType === "RESTAURANT" ? (
                <>
                  <path d="m16 2-2.3 2.3a3 3 0 0 0 0 4.2l1.8 1.8a3 3 0 0 0 4.2 0L22 8" />
                  <path d="M15 15 3.3 3.3a4.2 4.2 0 0 0 0 6l7.3 7.3c.7.7 2 .7 2.8 0L15 15Zm0 0 7 7" />
                  <path d="m2.1 21.8 6.4-6.3" />
                  <path d="m19 5-7 7" />
                </>
              ) : (
                <>
                  <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
                  <path d="M3 6h18" />
                  <path d="M16 10a4 4 0 0 1-8 0" />
                </>
              )}
            </svg>
          </div>
          <p className="pos-empty-state__title">No items in this category</p>
          <p className="pos-empty-state__subtitle">
            {retailType === "RESTAURANT"
              ? "Try a different filter, or pick another category from the left to see items here."
              : "Pick another category from the left, or adjust your search to see items here."}
          </p>
        </div>
      )}

      {/* Product detail drawer — retail lookup mode (no quantity → not cart mode) */}
      {detailProduct && (
        <CartItemDrawer
          cartItemModalOpen={!!detailProduct}
          setCartItemModalOpen={(open) => { if (!open) setDetailProduct(null); }}
          cartItemData={detailProduct}
        />
      )}
    </div>
  );
};

export default memo(CartProductSection);
